import React, { useState, useEffect } from 'react';
import { View, Text, TextInput, StyleSheet, Modal, TouchableOpacity } from 'react-native';
import { Picker } from '@react-native-picker/picker';
import BasicModalContainer from '../../../../../common/components/basicmodal/BasicModalContainer';
import ModalHeader from '../../../../../common/components/basicmodal/ModalHeader';
import ModalBody from '../../../../../common/components/basicmodal/ModalBody';
import ModalFooter from '../../../../../common/components/basicmodal/ModalFooter';
import { commonModalStyle } from '../../../../../common/components/basicmodal';

const AddPenaltyRuleModal = ({ isModalVisible, details, closeModal, onSave }) => {
  const [formData, setFormData] = useState({
    id: null,
    rule_name: '',
    type: 'battery',
    applies_to: 'all',
    calculation_type: 'fixed',
    amount: '',
    min_value: '',
    max_value: '',
    description: '',
    active: 1,
  });

  useEffect(() => {
    if (details) {
      setFormData({
        id: details.id,
        rule_name: details.rule_name || '',
        type: details.type || 'battery',
        applies_to: details.applies_to || 'all',
        calculation_type: details.calculation_type || 'fixed',
        amount: details.amount?.toString() || '',
        min_value: details.min_value?.toString() || '',
        max_value: details.max_value?.toString() || '',
        description: details.description || '',
        active: details.active || 1,
      });
    } else {
      setFormData({
        id: null,
        rule_name: '',
        type: 'battery',
        applies_to: 'all',
        calculation_type: 'fixed',
        amount: '',
        min_value: '',
        max_value: '',
        description: '',
        active: 1,
      });
    }
  }, [details, isModalVisible]);

  const handleSave = () => {
    const dataToSave: any = {
      rule_name: formData.rule_name,
      type: formData.type,
      applies_to: formData.applies_to,
      calculation_type: formData.calculation_type,
      amount: parseFloat(formData.amount) || 0,
      min_value: formData.min_value ? parseFloat(formData.min_value) : null,
      max_value: formData.max_value ? parseFloat(formData.max_value) : null,
      description: formData.description,
      active: formData.active,
      amount_type: 'fixed',
    };

    // Include id if editing existing record
    if (formData.id) {
      dataToSave.id = formData.id;
    }

    onSave(dataToSave);
  };

  return (
    <Modal animationType="none" transparent={true} visible={isModalVisible}>
      <BasicModalContainer contentStyle={{ width: 700, maxWidth: '95%' }}>
        <ModalHeader label={details ? 'Edit Penalty Rule' : 'Add Penalty Rule'} closeModal={closeModal} />
        <ModalBody>
          <View style={styles.container}>
            <View style={styles.row}>
              <View style={styles.column}>
                <Text style={styles.label}>Rule Name</Text>
                <TextInput
                  style={styles.input}
                  value={formData.rule_name}
                  onChangeText={(text) => setFormData({ ...formData, rule_name: text })}
                  placeholder="e.g., Late Return Fee"
                />
              </View>

              <View style={styles.column}>
                <Text style={styles.label}>Type</Text>
                <Picker
                  selectedValue={formData.type}
                  onValueChange={(value) => setFormData({ ...formData, type: value })}
                  style={styles.picker}
                >
                  <Picker.Item label="Battery" value="battery" />
                  <Picker.Item label="Mileage" value="mileage" />
                  <Picker.Item label="Delay" value="delay" />
                  <Picker.Item label="Damage" value="damage" />
                  <Picker.Item label="Violation" value="violation" />
                  <Picker.Item label="Delivery" value="delivery" />
                </Picker>
              </View>
            </View>

            <View style={styles.row}>
              <View style={styles.column}>
                <Text style={styles.label}>Applies To</Text>
                <Picker
                  selectedValue={formData.applies_to}
                  onValueChange={(value) => setFormData({ ...formData, applies_to: value })}
                  style={styles.picker}
                >
                  <Picker.Item label="All" value="all" />
                  <Picker.Item label="Hourly" value="hourly" />
                  <Picker.Item label="Daily" value="daily" />
                  <Picker.Item label="Weekly" value="weekly" />
                  <Picker.Item label="Monthly" value="monthly" />
                </Picker>
              </View>

              <View style={styles.column}>
                <Text style={styles.label}>Calculation Type</Text>
                <Picker
                  selectedValue={formData.calculation_type}
                  onValueChange={(value) => setFormData({ ...formData, calculation_type: value })}
                  style={styles.picker}
                >
                  <Picker.Item label="Fixed Amount" value="fixed" />
                  <Picker.Item label="Per Unit" value="per_unit" />
                  <Picker.Item label="Percentage" value="percentage" />
                </Picker>
              </View>
            </View>

            <View style={styles.row}>
              <View style={styles.column}>
                <Text style={styles.label}>Amount</Text>
                <TextInput
                  style={styles.input}
                  value={formData.amount}
                  onChangeText={(text) => setFormData({ ...formData, amount: text })}
                  placeholder="e.g., 50"
                  keyboardType="decimal-pad"
                />
              </View>

              <View style={styles.column}>
                <Text style={styles.label}>Min Value (Optional)</Text>
                <TextInput
                  style={styles.input}
                  value={formData.min_value}
                  onChangeText={(text) => setFormData({ ...formData, min_value: text })}
                  placeholder="e.g., 0"
                  keyboardType="decimal-pad"
                />
              </View>
            </View>

            <View style={styles.row}>
              <View style={styles.column}>
                <Text style={styles.label}>Max Value (Optional)</Text>
                <TextInput
                  style={styles.input}
                  value={formData.max_value}
                  onChangeText={(text) => setFormData({ ...formData, max_value: text })}
                  placeholder="e.g., 100"
                  keyboardType="decimal-pad"
                />
              </View>

              <View style={styles.column}>
                {/* Empty column for spacing */}
              </View>
            </View>

            <View style={styles.fullWidth}>
              <Text style={styles.label}>Description</Text>
              <TextInput
                style={[styles.input, styles.textArea]}
                value={formData.description}
                onChangeText={(text) => setFormData({ ...formData, description: text })}
                placeholder="Describe the penalty rule"
                multiline
                numberOfLines={3}
              />
            </View>
          </View>
        </ModalBody>
        <ModalFooter>
          <TouchableOpacity onPress={handleSave}>
            <Text style={styles.addButton}>{details ? 'Update' : 'Add'}</Text>
          </TouchableOpacity>
        </ModalFooter>
      </BasicModalContainer>
    </Modal>
  );
};

const styles = StyleSheet.create({
  ...commonModalStyle,
  container: {
    width: '100%',
  },
  row: {
    flexDirection: 'row',
    gap: 15,
    marginBottom: 10,
  },
  column: {
    flex: 1,
  },
  fullWidth: {
    width: '100%',
  },
  label: {
    fontSize: 14,
    fontWeight: 'bold',
    marginTop: 10,
    marginBottom: 5,
  },
  input: {
    borderWidth: 1,
    borderColor: '#ccc',
    borderRadius: 4,
    padding: 12,
    fontSize: 14,
  },
  textArea: {
    minHeight: 80,
    textAlignVertical: 'top',
  },
  picker: {
    borderWidth: 1,
    borderColor: '#ccc',
    borderRadius: 4,
    padding: 12,
    height: 45,
  },
});

export default AddPenaltyRuleModal;